import { create } from 'zustand';
import {
  defaultSegmentationDebugConfig,
  SegmentationDebugConfig,
  SegmentationDebugStep,
} from '@segmentation/dev/segmentationDebug';

export type DevLogKind = 'group' | 'groupEnd' | 'log' | 'warn' | 'table' | 'stop';

export interface DevLogEntry {
  id: number;
  time: string;
  kind: DevLogKind;
  tag: string;
  message: string;
  data?: unknown;
}

interface DevRuntimeState {
  enabled: boolean;
  logs: DevLogEntry[];
  pausedSegmentationStep: SegmentationDebugStep | null;
  segmentation: SegmentationDebugConfig;
  segmentationSceneCleaners: Partial<Record<keyof SegmentationDebugConfig['sceneLayers'], () => void>>;
  setEnabled: (enabled: boolean) => void;
  setPausedSegmentationStep: (step: SegmentationDebugStep | null) => void;
  setSegmentation: (patch: Partial<SegmentationDebugConfig>) => void;
  setSegmentationSceneLayer: (layer: keyof SegmentationDebugConfig['sceneLayers'], enabled: boolean) => void;
  setSegmentationSceneCleaner: (layer: keyof SegmentationDebugConfig['sceneLayers'], cleaner: (() => void) | null) => void;
  clearLogs: () => void;
  pushLog: (entry: Omit<DevLogEntry, 'id' | 'time'>) => void;
}

let nextLogId = 1;
let activePauseResolver: (() => void) | null = null;

export const useDevRuntimeStore = create<DevRuntimeState>((set) => ({
  enabled: false,
  logs: [],
  pausedSegmentationStep: null,
  segmentation: defaultSegmentationDebugConfig,
  segmentationSceneCleaners: {},
  setEnabled: (enabled) => set({ enabled }),
  setPausedSegmentationStep: (step) => set({ pausedSegmentationStep: step }),
  setSegmentation: (patch) =>
    set((state) => ({
      segmentation: {
        ...state.segmentation,
        ...patch,
        sceneLayers: {
          ...state.segmentation.sceneLayers,
          ...patch.sceneLayers,
        },
      },
    })),
  setSegmentationSceneLayer: (layer, enabled) =>
    set((state) => {
      if (!enabled) {
        state.segmentationSceneCleaners[layer]?.();
      }
      return {
        segmentation: {
          ...state.segmentation,
          sceneLayers: {
            ...state.segmentation.sceneLayers,
            [layer]: enabled,
          },
        },
      };
    }),
  setSegmentationSceneCleaner: (layer, cleaner) =>
    set((state) => {
      const nextCleaners = { ...state.segmentationSceneCleaners };
      if (cleaner) {
        nextCleaners[layer] = cleaner;
      } else {
        delete nextCleaners[layer];
      }
      return { segmentationSceneCleaners: nextCleaners };
    }),
  clearLogs: () => set({ logs: [] }),
  pushLog: (entry) =>
    set((state) => ({
      logs: [
        ...state.logs.slice(-499),
        {
          ...entry,
          id: nextLogId++,
          time: new Date().toLocaleTimeString(),
        },
      ],
    })),
}));

export const getDevRuntime = () => {
  const store = useDevRuntimeStore.getState();
  return {
    isEnabled: store.enabled,
    segmentation: store.segmentation,
    log(entry: Omit<DevLogEntry, 'id' | 'time'>) {
      useDevRuntimeStore.getState().pushLog(entry);
    },
    clearLogs() {
      useDevRuntimeStore.getState().clearLogs();
    },
    setSegmentationSceneCleaner(
      layer: keyof SegmentationDebugConfig['sceneLayers'],
      cleaner: (() => void) | null,
    ) {
      useDevRuntimeStore.getState().setSegmentationSceneCleaner(layer, cleaner);
    },
    pauseAtSegmentationStep(step: SegmentationDebugStep) {
      const runtimeStore = useDevRuntimeStore.getState();
      runtimeStore.setPausedSegmentationStep(step);
      runtimeStore.pushLog({
        kind: 'stop',
        tag: 'segmentJaw',
        message: `paused after ${step}`,
      });

      return new Promise<void>((resolve) => {
        activePauseResolver = () => {
          useDevRuntimeStore.getState().setPausedSegmentationStep(null);
          useDevRuntimeStore.getState().pushLog({
            kind: 'log',
            tag: 'segmentJaw',
            message: `continued after ${step}`,
          });
          activePauseResolver = null;
          resolve();
        };
      });
    },
  };
};

export function continuePausedSegmentationRun(): void {
  activePauseResolver?.();
}
